import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { clientApi } from "../Apis/Api";
import useDebounce from "../hook/useDebounce";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import PaginationControls from "../components/ui/PaginationControls";
import Breadcrumb from "../components/Breadcrumb";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import SortIcons from "../components/ui/SortIcons";
import { TextWrap } from "../utils/textWrap";
import ErrorAlert from "../components/ui/ErrorAlert";
import { apiCall } from "../Apis/apiCall";
import { handleApiResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal"; // 👈 NEW: Import InfoModal

const UsageHistoryPage = () => {
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [filters, setFilters] = useState({ status: "" });
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const fetchUsageHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await clientApi.getClientPlanUsageHistory({
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: searchQuery,
        plan: selectedPlan === "Select Plan" ? "" : selectedPlan,
        status: filters.status === "Select Status" ? "" : filters.status,
        ...(sortConfig.field && { sort_by: sortConfig.field }),
        ...(sortConfig.direction && { sort_order: sortConfig.direction }),
      });
      if (response.success && response.errorCode === 0) {
        let mappedData = response.data.map((record, index) => ({
          ...record,
          id: record.client_id || index + 1,
          usage_id: record.usage_id || index + 1,
          month: record.month ? formatMonth(record.month) : "-",
          client_name: record.client_name || "-",
          plan_name: record.plan_name || "-",
          total_vehicles_allowed: record.total_vehicles_allowed || 0,
          vehicle_count_parked: record.vehicle_count_parked || 0,
          threshold_allowed: record.threshold_allowed || 0,
          threshold_crossed_count: record.threshold_crossed_count || 0,
          billable_vehicles: record.billable_vehicles || 0,
          amount_due: record.amount_due || "-",
          invoice_status: record.invoice_status || "-",
          payment_date: record.payment_date || "-",
          paid_at: record.paid_at
            ? new Date(record.paid_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
          created_at: record.created_at
            ? new Date(record.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "-",
            payment_date:record?.payment_date || "-",
        }));

        // Client-side sorting as fallback
        if (sortConfig.field) {
          mappedData.sort((a, b) => {
            let aValue = a[sortConfig.field];
            let bValue = b[sortConfig.field];

            // Handle special cases for sorting
            if (sortConfig.field === "amount_due") {
              aValue =
                parseFloat((aValue || "").replace(/[^0-9.-]+/g, "")) || 0;
              bValue =
                parseFloat((bValue || "").replace(/[^0-9.-]+/g, "")) || 0;
            } else if (
              sortConfig.field === "month" ||
              sortConfig.field === "paid_at" ||
              sortConfig.field === "created_at"
            ) {
              aValue = new Date(aValue === "-" ? 0 : aValue);
              bValue = new Date(bValue === "-" ? 0 : bValue);
            } else if (typeof aValue === "string") {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
            }
            aValue = aValue || "";
            bValue = bValue || "";

            if (sortConfig.direction === "asc") {
              return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
              return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
          });
        }

        console.log("Sorted Usage History Data:", mappedData); // Debug log

        setUsageHistory(mappedData);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total || mappedData.length,
        }));
      } else {
        throw new Error("API returned an error");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to fetch usage history";
      setError(errorMessage);
      setUsageHistory([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
      // toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const usagePayment = async(usageID)=>{
    try {
      const res = await apiCall("post","/stripe/create-invoice-session",{usage_id:usageID})
       if (res.data.success && res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        throw new Error("Invalid response from checkout API");
      }
      handleApiResponse(res.data,"Usage Statistics","post")
    } catch (err) {
      const error = err.response?.data || err.message;
      handleApiResponse(error,"Usage Statistics","post")
    }
  }

  const debouncedSearch = useDebounce(searchQuery, 500);

  useEffect(() => {
    fetchUsageHistory();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    selectedPlan,
    filters.status,
    sortConfig.field,
    sortConfig.direction,
  ]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusChange = (status) => {
    setFilters((prev) => ({ ...prev, status }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleAddRecord = () => {
    toast.info("Add new record functionality to be implemented");
  };

  const formatMonth = (monthString) => {
    if (!monthString || monthString === "-") return "-";
    const date = new Date(`${monthString}-01`);
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

   const today = new Date();
      const currentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchUsageHistory} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search By Client Name, Plan Name or Month"
                value={searchQuery}
                onChange={handleSearch}
                ref={searchInputRef}
                aria-label="Search by client name, plan name or month"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          {/* 👈 NEW: Info Button */}
          <button
            type="button"
            className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Information about Usage Statistics"
            onClick={handleInfoClick}
            aria-label="Page Information"
          >
            <Icon icon="lucide:info" className="menu-icon" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Usage Statistics" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {usageHistory.length === 0 && !loading ? (
            <TableDataNoAvailable message="No usage history available." />
          ) : (
            <table
              className="table bordered-table sm-table mb-0 table-hover"
              aria-label="Usage history table"
            >
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("month")}
                    role="button"
                    aria-label={`Sort by month, currently sorted ${
                      sortConfig.field === "month"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Month
                      <SortIcons field="month" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("client_name")}
                    role="button"
                    aria-label={`Sort by client name, currently sorted ${
                      sortConfig.field === "client_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Client Name
                      <SortIcons field="client_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("plan_name")}
                    role="button"
                    aria-label={`Sort by plan name, currently sorted ${
                      sortConfig.field === "plan_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Plan Name
                      <SortIcons field="plan_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("total_vehicles_allowed")}
                    role="button"
                    aria-label={`Sort by vehicle count allowed, currently sorted ${
                      sortConfig.field === "total_vehicles_allowed"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Vehicle Count Allowed
                      <SortIcons
                        field="total_vehicles_allowed"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("vehicle_count_parked")}
                    role="button"
                    aria-label={`Sort by vehicle count allowed, currently sorted ${
                      sortConfig.field === "vehicle_count_parked"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Vehicle Count Parked
                      <SortIcons
                        field="vehicle_count_parked"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("threshold_allowed")}
                    role="button"
                    aria-label={`Sort by threshold crossed count, currently sorted ${
                      sortConfig.field === "threshold_allowed"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                     Threshold Allowed
                      <SortIcons
                        field="threshold_allowed"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("threshold_crossed_count")}
                    role="button"
                    aria-label={`Sort by threshold crossed count, currently sorted ${
                      sortConfig.field === "threshold_crossed_count"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Threshold Crossed
                      <SortIcons
                        field="threshold_crossed_count"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("billable_vehicles")}
                    role="button"
                    aria-label={`Sort by billable vehicles, currently sorted ${
                      sortConfig.field === "billable_vehicles"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Billable Vehicles
                      <SortIcons
                        field="billable_vehicles"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("amount_due")}
                    role="button"
                    aria-label={`Sort by amount due, currently sorted ${
                      sortConfig.field === "amount_due"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Amount Due
                      <SortIcons field="amount_due" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("invoice_status")}
                    role="button"
                    aria-label={`Sort by invoice status, currently sorted ${
                      sortConfig.field === "invoice_status"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Invoice Status
                      <SortIcons
                        field="invoice_status"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_at")}
                    role="button"
                    aria-label={`Sort by created_at, currently sorted ${
                      sortConfig.field === "created_at"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created At
                      <SortIcons
                        field="created_at"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("payment_date")}
                    role="button"
                    aria-label={`Sort by payment_date, currently sorted ${
                      sortConfig.field === "payment_date"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Payment Date
                      <SortIcons
                        field="payment_date"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("paid_at")}
                    role="button"
                    aria-label={`Sort by paid at, currently sorted ${
                      sortConfig.field === "paid_at"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Paid At
                      <SortIcons field="paid_at" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {usageHistory.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.month}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.client_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.plan_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.total_vehicles_allowed}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.vehicle_count_parked}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.threshold_allowed}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.threshold_crossed_count}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.billable_vehicles}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.amount_due}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.invoice_status}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.created_at}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.payment_date}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {record.paid_at}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-primary-600 text-md mb-0 fw-normal text-secondary-light" disabled={
  !(record.payment_date && new Date(record.payment_date) < new Date(currentDate) &&
    (record.invoice_status || "") === "sent" &&
    Number((record.amount_due || "").replace(/[^0-9.-]+/g, "")) > 0)
} onClick={()=>usagePayment(record.usage_id)}>
                        Pay now
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 NEW: Info Modal - EXACTLY SAME STYLE AS Other Pages */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={handleInfoClose}
        title="Usage Statistics - Complete Overview"
      >
        <p className="text-neutral-900 mb-4">
          This page is your <strong>billing and usage dashboard</strong> showing exactly how your parking management system is being used and what you're being charged for. 
          Think of it as your <strong>monthly usage statement</strong> with detailed breakdowns.
        </p>

        <h6 className="fw-bold text-primary-600 mb-3">What Each Column Means</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item"><strong>Month</strong> – The billing period (e.g., January 2024)</li>
          <li className="list-group-item"><strong>Client Name</strong> – Your business/organization name</li>
          <li className="list-group-item"><strong>Plan Name</strong> – Your current subscription plan</li>
          <li className="list-group-item"><strong>Vehicle Count Allowed</strong> – Maximum vehicles included in your plan</li>
          <li className="list-group-item"><strong>Threshold Crossed</strong> – How many times you exceeded your limit</li>
          <li className="list-group-item"><strong>Billable Vehicles</strong> – Actual vehicles processed (including overages)</li>
          <li className="list-group-item"><strong>Amount Due</strong> – Total charges for the month</li>
          <li className="list-group-item"><strong>Invoice Status</strong> – Current payment status (Sent, Paid, Pending)</li>
          <li className="list-group-item"><strong>Paid At</strong> – When payment was processed (if applicable)</li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Understanding Your Billing</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Base Plan Charges</strong><br/>
            • Fixed monthly fee for your selected plan<br/>
            • Includes predetermined vehicle capacity<br/>
            • Charged regardless of actual usage
          </li>
          <li className="list-group-item">
            <strong>Overage Charges</strong><br/>
            • Additional fees when you exceed your limit<br/>
            • Calculated per extra vehicle processed<br/>
            • Only charged if you go over your allowance
          </li>
          <li className="list-group-item">
            <strong>Payment Timeline</strong><br/>
            • Invoices generated at month end<br/>
            • Due dates based on your billing cycle<br/>
            • Automatic reminders for pending payments
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Pay Now Button - When It Works</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Active When:</strong><br/>
            • Invoice is sent and unpaid<br/>
            • Payment due date has passed<br/>
            • Amount due is greater than zero<br/>
            • System automatically enables the button
          </li>
          <li className="list-group-item">
            <strong>Disabled When:</strong><br/>
            • Invoice is already paid<br/>
            • Payment is not yet due<br/>
            • No amount is due<br/>
            • Invoice is still being processed
          </li>
          <li className="list-group-item">
            <strong>Payment Process:</strong><br/>
            • Click "Pay Now" → Secure Stripe checkout<br/>
            • Multiple payment methods accepted<br/>
            • Instant confirmation and receipt<br/>
            • Status updates automatically
          </li>
        </ul>

        <h6 className="fw-bold text-primary-600 mb-3">Monitoring Your Usage</h6>
        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item">
            <strong>Stay Within Limits</strong><br/>
            • Monitor "Vehicle Count Allowed" vs "Billable Vehicles"<br/>
            • Plan ahead for busy months<br/>
            • Consider upgrading if consistently exceeding limits
          </li>
          <li className="list-group-item">
            <strong>Cost Optimization</strong><br/>
            • Analyze usage patterns monthly<br/>
            • Adjust plans based on actual needs<br/>
            • Contact support for plan recommendations
          </li>
          <li className="list-group-item">
            <strong>Payment Management</strong><br/>
            • Set payment reminders<br/>
            • Keep track of due dates<br/>
            • Maintain updated payment methods
          </li>
        </ul>

        <div className="alert alert-info small mt-4">
          <strong>Pro Tips:</strong><br/>
          • Check this page regularly to avoid surprise charges<br/>
          • Upgrade your plan before busy seasons to save on overage fees<br/>
          • Set calendar reminders for payment due dates<br/>
          • Download monthly statements for accounting purposes<br/>
          • Contact support if you notice unusual usage patterns
        </div>

        <p className="text-neutral-900 mt-4 fw-bold text-primary">
          Remember: Proactive usage monitoring = Cost control + Better budgeting + No service interruptions!
        </p>
        <p className="text-neutral-900 small">
          Business Impact: Clear billing transparency helps with financial planning, timely payments ensure uninterrupted service, and usage awareness enables optimal resource allocation.
        </p>
      </InfoModal>
    </>
  );
};

export default UsageHistoryPage;